import React from 'react';

import Avatar from '@atlaskit/avatar';
import { cssMap } from '@atlaskit/css';
import FolderFilledIcon from '@atlaskit/icon/core/folder-closed';
import BoardIcon from '@atlaskit/icon/core/migration/board';
import FolderFilledIconLegacy from '@atlaskit/icon/glyph/folder-filled';
import { SkeletonItem } from '@atlaskit/menu';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ItemWrapper } from '@atlassian/people-teams-ui/profile-layout';
import {
	AnalyticsEventActions,
	AnalyticsEventSource,
	ProductIcon,
	ShortProductKey,
	useAnalytics,
} from '@atlassian/ptc-common';

import { type PlaceItem } from '../ProfilePlaces/types';

import * as Styled from './ProfilePlacesItem.styled';

const styles = cssMap({
	skeletonItem: {
		minHeight: '16px',
		paddingInline: token('space.0', '0px'),
	},
	skeletonItemWithIcon: {
		minHeight: '28px',
		paddingInline: token('space.0', '0px'),
	},
	skeletonItemSpacing: {
		marginBlockEnd: token('space.050', '4px'),
	},
});

export const PlaceLink = Styled.PlaceLink;

const MainIcon = ({ icon, type }: PlaceItem) => {
	if (icon) {
		return (
			<Styled.BaseIcon>
				{/* If Avatar cannot load image from `iconUrl`, it will show a default icon (a ship icon) */}
				<Avatar size="small" src={icon} appearance="square" />
			</Styled.BaseIcon>
		);
	}

	if (type === 'space') {
		return (
			<Styled.SpaceIcon>
				<FolderFilledIcon
					LEGACY_fallbackIcon={FolderFilledIconLegacy}
					color="currentColor"
					LEGACY_size="medium"
					spacing="spacious"
					label=""
				/>
			</Styled.SpaceIcon>
		);
	} else if (type === 'board') {
		return (
			<Styled.BoardIcon>
				<BoardIcon color="currentColor" LEGACY_size="medium" spacing="spacious" label="" />
			</Styled.BoardIcon>
		);
	}

	return null;
};

const HeaderIcon = ({ type }: Pick<PlaceItem, 'type'>) => {
	switch (type) {
		case 'space':
			return <ProductIcon size="xxsmall" productKey={ShortProductKey.CONFLUENCE} />;
		case 'board':
		case 'project':
			return <ProductIcon size="xxsmall" productKey={ShortProductKey.JIRA} />;
		default:
			return null;
	}
};

function typeToTitle(type: 'space' | 'board' | 'project') {
	switch (type) {
		case 'space':
			return 'Confluence';
		case 'board':
		case 'project':
			return 'Jira';
		default:
			return '';
	}
}

export const ProfilePlacesItem = (props: PlaceItem) => {
	const analytics = useAnalytics();

	const { name, path, type, hasLoaded } = props;

	const handleAnchorClick = () => {
		analytics.pushUIEvent({
			source: AnalyticsEventSource.USER_PROFILE_SCREEN,
			actionSubject: 'placesLink',
			action: AnalyticsEventActions.CLICKED,
			attributes: {
				type,
			},
		});
	};

	return (
		<PlacesItem>
			{hasLoaded ? (
				<>
					<Styled.Header>
						<Styled.HeaderIcon>
							<HeaderIcon type={type} />
						</Styled.HeaderIcon>
						<Styled.HeaderTitle>{typeToTitle(type)}</Styled.HeaderTitle>
					</Styled.Header>
					<Styled.Content>
						<MainIcon {...props} />
						<Styled.Details>
							<PlaceLink href={path} onClick={handleAnchorClick}>
								{name}
							</PlaceLink>
						</Styled.Details>
					</Styled.Content>
				</>
			) : (
				<>
					<Box xcss={styles.skeletonItemSpacing}>
						<SkeletonItem isShimmering width="150px" xcss={styles.skeletonItem} />
					</Box>
					<SkeletonItem hasIcon isShimmering xcss={styles.skeletonItemWithIcon} />
				</>
			)}
		</PlacesItem>
	);
};

const PlacesItem = ({ children }: { children: React.ReactNode }) =>
	fg('user-profile-refresh') ? (
		<ItemWrapper>{children}</ItemWrapper>
	) : (
		<Styled.PlacesItem>{children}</Styled.PlacesItem>
	);

export default ProfilePlacesItem;
